import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useForm } from 'react-hook-form';

import { Button, FormInput } from '../../../components';
import { REFUND_TYPE } from '../../../config';
import { refundDetailsSchema } from '../../../utils';
import { DetailRow } from './detail-row';
import { amountByCurrency, detailDate, fiatSuffix, rateDisplay } from './refund-format';

const hasValue = (value) => value !== undefined && value !== null && value !== '';
const firstValue = (...values) => values.find(hasValue);
const firstCurrency = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim());

/**
 * Step 2 (refund branches) — read-only dispute/refund details + the wallet
 * form. Expected/actual rows can render from the dispute row immediately;
 * refund currency/rate/calculated rows are enriched by the wallet-less preview.
 * The create endpoint validates the submitted wallet.
 */
const RefundDetailsStep = ({
  dispute,
  refundType,
  preview,
  isPreviewing,
  previewError,
  walletNetwork,
  onReview,
  onBack
}) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({ mode: 'onChange', resolver: yupResolver(refundDetailsSchema) });

  const isExcess = refundType === REFUND_TYPE.OVERPAYMENT_EXCESS;
  const isUnderpayment = refundType === REFUND_TYPE.UNDERPAYMENT_FULL;
  const balanceLabel = isUnderpayment ? 'EXPECTED BALANCE' : 'EXCESS AMOUNT';
  const previewTransaction = preview?.transaction || {};
  const sourceCurrency = preview?.source_currency;
  const settlementCurrency = sourceCurrency || dispute?.currency_slug;
  const rateCurrency = sourceCurrency || preview?.payout_currency || dispute?.currency_slug;
  const expected = firstValue(
    previewTransaction.payment_amount,
    dispute?.expected_amount,
    dispute?.payment_amount,
    dispute?.transaction_payment_amount,
    dispute?.transaction?.payment_amount
  );
  const actualPaid = firstValue(
    previewTransaction.amount_paid,
    dispute?.actual_amount_paid,
    dispute?.actual_paid_amount,
    dispute?.amount_paid,
    dispute?.transaction_amount_paid,
    dispute?.transaction?.amount_paid
  );
  const paymentCurrency =
    firstCurrency(
      previewTransaction.payment_currency_slug,
      previewTransaction.payment_currency,
      dispute?.payment_currency_slug,
      dispute?.payment_currency,
      dispute?.expected_currency,
      dispute?.currency_slug
    ) || settlementCurrency;
  const refundCurrency = preview?.payout_currency || paymentCurrency || settlementCurrency;
  const txCurrency = refundCurrency;
  // Dispute refunds pay out in the same currency that was paid, so there is no
  // FX conversion — the rate is 1 whenever the preview can't supply one.
  const conversionRate = preview?.conversion_rate ?? 1;
  const grossRefundable = isExcess ? dispute?.excess_amount ?? dispute?.amount : actualPaid;
  const calculatedRefundRaw = sourceCurrency
    ? preview?.source_amount
    : firstValue(preview?.payout_amount, dispute?.refund_amount, grossRefundable);
  const calculatedRefundValue = hasValue(calculatedRefundRaw)
    ? sourceCurrency
      ? fiatSuffix(calculatedRefundRaw, sourceCurrency)
      : amountByCurrency(calculatedRefundRaw, refundCurrency)
    : null;

  const dash = '--';
  const validAmount = isExcess ? expected : actualPaid;
  let subtitle;
  if (isUnderpayment) {
    subtitle =
      'This returns the exact amount received. The transaction will not be settled after a full refund.';
  } else if (isExcess) {
    subtitle = (
      <>
        You&apos;re refunding only the <strong>excess</strong> overpaid amount.
        {hasValue(validAmount) ? (
          <>
            {' '}
            The valid <strong>{amountByCurrency(validAmount, txCurrency)}</strong> has been settled
            in your account and the customer will bear the fee for this refund.
          </>
        ) : (
          ' The customer will bear the fee for this refund.'
        )}
      </>
    );
  } else {
    subtitle = (
      <>
        {hasValue(validAmount) ? (
          <>
            The valid <strong>{amountByCurrency(validAmount, txCurrency)}</strong> will be refunded
          </>
        ) : (
          'The full amount will be refunded'
        )}{' '}
        to the customer and they will bear the fee for this refund.
      </>
    );
  }

  const walletValue = watch('wallet_address');
  // The details/summary screens render from the dispute (+ rate 1), so the
  // review step no longer waits on the preview response.
  const canReview = Boolean(walletValue) && !isPreviewing;
  const network = walletNetwork || preview?.wallet_network || '';

  return (
    <form className="resolve resolve--details" onSubmit={handleSubmit(onReview)}>
      <p className="resolve__subtitle">{subtitle}</p>

      <div className="resolve__rows resolve__rows--bordered">
        <DetailRow label="CUSTOMER ID" value={dispute?.customer_email} />
        <DetailRow label="DESCRIPTION" value={dispute?.description} />
        <DetailRow
          label="EXPECTED AMOUNT"
          testId="row-expected-amount"
          value={hasValue(expected) ? amountByCurrency(expected, txCurrency) : dash}
        />
        <DetailRow
          label="ACTUAL AMOUNT PAID"
          testId="row-actual-amount"
          value={hasValue(actualPaid) ? amountByCurrency(actualPaid, txCurrency) : dash}
        />
        <DetailRow
          label={balanceLabel}
          value={amountByCurrency(
            dispute?.excess_amount ?? dispute?.amount,
            dispute?.excess_currency ?? dispute?.currency_slug
          )}
        />
        <DetailRow label="REFERENCE" value={dispute?.reference} />
        <DetailRow label="DATE" value={detailDate(dispute?.created_at)} />
        <DetailRow
          label="REFUND CURRENCY"
          info="Currency the customer is refunded in"
          value={(refundCurrency || '').toUpperCase() || dash}
        />
        <DetailRow
          label="CURRENT EXCHANGE RATE"
          value={refundCurrency ? rateDisplay(refundCurrency, conversionRate, rateCurrency) : dash}
        />
        <DetailRow label="CALCULATED REFUND VALUE" value={calculatedRefundValue || dash} />
        <DetailRow label="DISPUTE TYPE" value={dispute?.dispute_type} />
      </div>

      <FormInput
        label="Customer's wallet address"
        name="wallet_address"
        placeholder="Please enter customer's wallet address"
        error={(errors.wallet_address && errors.wallet_address.message) || previewError}
        register={register('wallet_address')}
        data-testid="refund-wallet-address"
      />
      <FormInput
        label="Network"
        name="wallet_network"
        value={network}
        disabled
        readOnly
        data-testid="refund-wallet-network"
      />
      <FormInput
        label="Reason for refund"
        name="reason"
        type="textarea"
        placeholder="e.g Customer requested cancellation"
        error={errors.reason && errors.reason.message}
        register={register('reason')}
        data-testid="refund-reason"
      />

      <div className="resolve__actions">
        <Button variant="outline" type="button" onClick={onBack} data-testid="refund-back-btn">
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit(onReview)}
          disabled={!canReview}
          isLoading={isPreviewing}
          data-testid="refund-review-btn"
        >
          Review refund
        </Button>
      </div>
    </form>
  );
};

RefundDetailsStep.propTypes = {
  dispute: PropTypes.object,
  refundType: PropTypes.string,
  preview: PropTypes.object,
  isPreviewing: PropTypes.bool,
  previewError: PropTypes.string,
  walletNetwork: PropTypes.string,
  onReview: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export { RefundDetailsStep };
